import { Logger } from "./core/logger";
import { HttpClient } from "./core/http";
import { CookieManager } from "./core/cookie";
import { parseTrackingParams, stripTrackingParams } from "./core/url";
import { SessionManager } from "./core/session";
import { EventQueue } from "./core/events";
import { AutoTracker } from "./core/autotrack";
import {
  COOKIE_REF,
  COOKIE_DISCOUNT,
  COOKIE_TOKEN,
  DEFAULT_COOKIE_DURATION_DAYS,
  API_PATH_INIT,
  API_PATH_TRACK_CLICK,
  API_PATH_IDENTIFY,
  API_PATH_BATCH_EVENTS,
  API_PATH_DISCOUNT_LOOKUP,
  API_PATH_TRACK_PAGEVIEW,
  API_PATH_DISCOUNT_VALIDATE,
  DEFAULT_TRACKING_DOMAIN,
  DEFAULT_API_SECRET,
} from "./core/config";
import { ConversionTracker } from "./core/conversion";
import type {
  SDKInitConfig,
  SDKInitResponse,
  TrackClickResponse,
  IdentifyResponse,
  ConversionData,
  ConversionResponse,
  DiscountInfo,
  DiscountLookupResponse,
  DiscountValidationResult,
  DiscountValidationAPIResponse,
  BatchEvent,
  Customer,
  AddToCartData,
  RemoveFromCartData,
  ProductViewData,
  ThankYouViewData,
  CheckoutData,
  TrackingParams,
} from "./types";

import {
  buildPageviewPayload,
  validatePageviewPayload,
  initEngagementTracking,
  resetEngagementMetrics,
} from "./utils/trackPageview";

export class AffiliateSDK {
  private initialized = false;
  private isDormant = false;
  private affiliateCode: string | null = null;
  private cookieToken: string | null = null;
  private discountCode: string | null = null;
  private isAttributed = false;
  private http: HttpClient | null = null;
  private cookieDuration = DEFAULT_COOKIE_DURATION_DAYS;
  private discountCache: DiscountInfo | null = null;
  private conversionTracker: ConversionTracker | null = null;
  private locationId: string | null = null;
  private locationName: string | null = null;

  private setTokenCookie(token: string, days: number): void {
    CookieManager.set(COOKIE_TOKEN, token, days);
    try {
      const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      localStorage.setItem(COOKIE_TOKEN, JSON.stringify({ token, expires_at: expiresAt }));
    } catch {
      // localStorage unavailable or full
    }
  }

  private getTokenCookie(): string | null {
    const cookieVal = CookieManager.get(COOKIE_TOKEN);
    if (cookieVal) return cookieVal;

    // Fallback: localStorage (Safari ITP may have expired the JS cookie after 7 days)
    try {
      const stored = localStorage.getItem(COOKIE_TOKEN);
      if (stored) {
        const { token, expires_at } = JSON.parse(stored) as { token: string; expires_at: string };
        const remainingMs = new Date(expires_at).getTime() - Date.now();
        if (remainingMs > 0) {
          const remainingDays = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
          this.setTokenCookie(token, remainingDays);
          return token;
        }
        localStorage.removeItem(COOKIE_TOKEN);
      }
    } catch {
      // localStorage unavailable
    }

    return null;
  }

  private async handleNewClick(
    ref: string,
    discount: string | null,
    trackingParams: TrackingParams,
    serverCt: string | null,
    locationId?: string | null,
    locationName?: string | null,
  ): Promise<void> {
    if (!this.http) return;

    CookieManager.set(COOKIE_REF, ref, this.cookieDuration);
    this.affiliateCode = ref;

    // Pre-emptively store the server-issued token so attribution survives a
    // navigation that interrupts the /track/click request.
    if (serverCt) {
      this.setTokenCookie(serverCt, this.cookieDuration);
      this.cookieToken = serverCt;
    }

    if (discount) {
      CookieManager.set(COOKIE_DISCOUNT, discount, this.cookieDuration);
      this.discountCode = discount;
    } else {
      CookieManager.delete(COOKIE_DISCOUNT);
      this.discountCode = null;
    }
    const sessionId = SessionManager.getOrCreate();

    try {
      const clickPayload: Record<string, unknown> = {
        session_id: sessionId,
        tracking_code: ref,
        referrer_url: typeof document !== "undefined" ? document.referrer : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        ip_address: "",
        page_url: typeof window !== "undefined" ? window.location.href : "",
        utm_source: trackingParams.utm_source,
        utm_medium: trackingParams.utm_medium,
        utm_campaign: trackingParams.utm_campaign,
        utm_content: trackingParams.utm_content,
        utm_term: trackingParams.utm_term,
        gclid: trackingParams.gclid,
        fbclid: trackingParams.fbclid,
        msclkid: trackingParams.msclkid,
      };
      if (locationId) clickPayload.location_id = locationId;
      if (locationName) clickPayload.location_name = locationName;
      const clickResponse = await this.http.post<TrackClickResponse>(
        API_PATH_TRACK_CLICK,
        clickPayload,
      );

      this.cookieToken = clickResponse.cookie_token;
      this.setTokenCookie(clickResponse.cookie_token, this.cookieDuration);
      this.isAttributed = true;
      Logger.log("Click tracked successfully", clickResponse);
    } catch {
      Logger.warn("Click tracking failed — ref cookie still saved");
      this.isAttributed = true;
      if (!serverCt) {
        const fallbackToken = SessionManager.getOrCreate();
        this.cookieToken = fallbackToken;
        this.setTokenCookie(fallbackToken, this.cookieDuration);
        Logger.log("Click tracking failed - using session_id as cookie_token fallback");
      } else {
        Logger.log("Click tracking failed - using server ct token as cookie_token fallback");
      }
    }
  }

  private async handleExistingVisitor(): Promise<void> {
    if (!this.http) return;

    const savedToken = this.getTokenCookie();
    const savedRef = CookieManager.get(COOKIE_REF);
    if (savedToken) {
      this.cookieToken = savedToken;
    } else {
      const sessionId = SessionManager.getOrCreate();
      this.cookieToken = sessionId;
      this.setTokenCookie(sessionId, this.cookieDuration);
    }

    if (savedRef) {
      this.affiliateCode = savedRef;
      this.isAttributed = true;
    }

    const savedDiscount = CookieManager.get(COOKIE_DISCOUNT);
    if (savedDiscount) {
      this.discountCode = savedDiscount;
    }

    Logger.log("Existing visitor loaded from cookies", {
      affiliateCode: this.affiliateCode,
      discountCode: this.discountCode,
      isAttributed: this.isAttributed,
    });
  }

  private fireOnReady(config: SDKInitConfig): void {
    if (typeof config.onReady === "function") {
      try {
        config.onReady(this);
      } catch (err) {
        Logger.error("onReady callback threw an error", err);
      }
    }
  }

  async init(config: SDKInitConfig): Promise<void> {
    if (this.initialized) {
      Logger.warn("SDK already initialized — ignoring duplicate init() call");
      return;
    }
    this.initialized = true;

    if (!config.apiKey || typeof config.apiKey !== "string") {
      Logger.error("Missing required config: apiKey — SDK entering dormant mode");
      this.isDormant = true;
      this.initialized = true;
      return;
    }

    try {
      Logger.setDebug(config.debug === true);
      Logger.log("Initializing SDK...", {
        trackingDomain: DEFAULT_TRACKING_DOMAIN,
        enablePageviews: config.enablePageviews,
      });

      if (typeof config.cookieDuration === "number" && config.cookieDuration > 0) {
        this.cookieDuration = config.cookieDuration;
      }

      if (config.location_id) this.locationId = config.location_id;
      if (config.location_name) this.locationName = config.location_name;

      initEngagementTracking();

      this.http = new HttpClient({
        baseURL: DEFAULT_TRACKING_DOMAIN,
        apiKey: config.apiKey,
        apiSecret: DEFAULT_API_SECRET,
      });

      this.conversionTracker = new ConversionTracker(this.http, config.apiKey);

      try {
        const initParams: Record<string, string> = {};
        if (this.locationId) initParams.location_id = this.locationId;
        if (this.locationName) initParams.location_name = this.locationName;
        const serverConfig = await this.http.get<SDKInitResponse>(
          API_PATH_INIT,
          Object.keys(initParams).length ? initParams : undefined,
        );
        Logger.log("Server config received", serverConfig);

        if (serverConfig.cookie_duration > 0) {
          this.cookieDuration = serverConfig.cookie_duration;
        }
      } catch {
        Logger.warn("SDK init API failed — entering dormant mode");
        this.isDormant = true;
        this.initialized = true;
        this.fireOnReady(config);
        return;
      }

      const trackingParams = parseTrackingParams();
      const savedRef = CookieManager.get(COOKIE_REF);
      const savedToken = this.getTokenCookie();

      if (trackingParams.ct) {
        if (trackingParams.ct === savedToken) {
          // Server token matches stored token — exact dedup, skip /track/click
          Logger.log("Click already tracked — ct token matches stored token", {
            ct: trackingParams.ct,
          });
          this.affiliateCode = savedRef;
          this.isAttributed = !!savedRef;
          this.cookieToken = savedToken;
          if (trackingParams.discount) {
            CookieManager.set(COOKIE_DISCOUNT, trackingParams.discount, this.cookieDuration);
            this.discountCode = trackingParams.discount;
          } else {
            const savedDiscount = CookieManager.get(COOKIE_DISCOUNT);
            if (savedDiscount) this.discountCode = savedDiscount;
          }
        } else if (trackingParams.ref) {
          // Server issued a new token — new click, last-click attribution wins
          Logger.log("New click token detected — overriding previous attribution", {
            previousToken: savedToken,
            newCt: trackingParams.ct,
            ref: trackingParams.ref,
          });
          await this.handleNewClick(
            trackingParams.ref,
            trackingParams.discount,
            trackingParams,
            trackingParams.ct,
            this.locationId,
            this.locationName,
          );
        } else {
          // ct without ref — adopt server token, preserve existing ref attribution
          this.setTokenCookie(trackingParams.ct, this.cookieDuration);
          this.cookieToken = trackingParams.ct;
          if (savedRef) {
            this.affiliateCode = savedRef;
            this.isAttributed = true;
          }
          const savedDiscount = CookieManager.get(COOKIE_DISCOUNT);
          if (savedDiscount) this.discountCode = savedDiscount;
        }
      } else if (trackingParams.ref) {
        if (savedRef === trackingParams.ref && savedToken) {
          // Same ref already tracked — dedup by ref
          Logger.log("Click already tracked for this ref", { ref: savedRef });
          this.affiliateCode = savedRef;
          this.isAttributed = true;
          this.cookieToken = savedToken;
          if (trackingParams.discount) {
            CookieManager.set(COOKIE_DISCOUNT, trackingParams.discount, this.cookieDuration);
            this.discountCode = trackingParams.discount;
          } else {
            const savedDiscount = CookieManager.get(COOKIE_DISCOUNT);
            if (savedDiscount) this.discountCode = savedDiscount;
          }
        } else {
          // New or different ref — last-click wins, track fresh click
          Logger.log("New affiliate ref detected — overriding previous attribution", {
            previous: savedRef,
            new: trackingParams.ref,
          });
          await this.handleNewClick(
            trackingParams.ref,
            trackingParams.discount,
            trackingParams,
            null,
            this.locationId,
            this.locationName,
          );
        }
      } else {
        // No tracking params in URL
        await this.handleExistingVisitor();
      }

      // Strip affiliate tracking params from URL bar so they don't leak into
      // analytics, bookmarks, or shared links.
      stripTrackingParams();

      SessionManager.getOrCreate();

      const beaconUrl = `${this.http.getBaseURL()}${API_PATH_BATCH_EVENTS}?api_key=${encodeURIComponent(config.apiKey)}`;
      EventQueue.init(async (events: BatchEvent[]) => {
        if (!this.http) return false;
        try {
          await this.http.post(API_PATH_BATCH_EVENTS, { events });
          return true;
        } catch {
          return false;
        }
      }, beaconUrl);

      Logger.log("SDK initialized successfully", {
        isAttributed: this.isAttributed,
        affiliateCode: this.affiliateCode,
        discountCode: this.discountCode,
      });

      if (config.enablePageviews) {
        this.trackPageview().catch(() => {});

        AutoTracker.start(() => {
          this.trackPageview().catch(() => {});
        });
      }

      this.fireOnReady(config);
    } catch (err) {
      Logger.error("Unexpected error during init — entering dormant mode", err);
      this.isDormant = true;
      this.initialized = true;
      this.fireOnReady(config);
    }
  }

  hasAffiliate(): boolean {
    if (!this.initialized || this.isDormant) return false;
    return this.isAttributed;
  }

  getAffiliateCode(): string | null {
    if (!this.initialized || this.isDormant) return null;
    return this.affiliateCode;
  }

  async getDiscount(): Promise<DiscountInfo | null> {
    if (!this.initialized || this.isDormant || !this.http) return null;
    if (!this.isAttributed || !this.discountCode) return null;
    if (this.discountCache) return this.discountCache;

    try {
      const response = await this.http.get<DiscountLookupResponse>(
        `${API_PATH_DISCOUNT_LOOKUP}/${encodeURIComponent(this.discountCode)}`,
      );

      this.discountCache = {
        code: response.code,
        type: response.type,
        value: response.value,
        min_order: response.min_order,
        description: response.description,
      };

      Logger.log("Discount fetched", this.discountCache);
      return this.discountCache;
    } catch (err) {
      Logger.warn("Failed to fetch discount info", err);
      return null;
    }
  }

  async trackPageview(): Promise<void> {
    if (!this.initialized || this.isDormant || !this.http) return;
    if (typeof window === "undefined" || !window.location) {
      Logger.warn("trackPageview skipped — no window.location");
      return;
    }

    try {
      const sessionId = SessionManager.getOrCreate();
      resetEngagementMetrics();
      const payload = buildPageviewPayload(
        this.cookieToken || "",
        sessionId,
        this.locationId,
        this.locationName,
      );
      if (!validatePageviewPayload(payload)) {
        Logger.error("Invalid pageview payload, skipping request");
        return;
      }
      await this.http.post(API_PATH_TRACK_PAGEVIEW, payload);
      Logger.log(`Pageview tracked: ${window.location.pathname}`);
    } catch (err) {
      Logger.warn("Pageview tracking failed", err);
    }
  }

  async trackOrder(orderData: ConversionData): Promise<ConversionResponse | null> {
    if (!this.initialized || this.isDormant || !this.conversionTracker) return null;
    return this.conversionTracker.trackOrder(
      orderData,
      this.cookieToken,
      this.isAttributed,
      this.locationId,
      this.locationName,
    );
  }

  async trackCheckout(data: CheckoutData): Promise<void> {
    if (!this.initialized || this.isDormant || !this.conversionTracker) return;
    return this.conversionTracker.trackCheckout(
      data,
      this.cookieToken,
      this.locationId,
      this.locationName,
    );
  }

  async trackAddToCart(data: AddToCartData): Promise<void> {
    if (!this.initialized || this.isDormant || !this.conversionTracker) return;
    return this.conversionTracker.trackAddToCart(
      data,
      this.cookieToken,
      this.locationId,
      this.locationName,
    );
  }

  async trackRemoveFromCart(data: RemoveFromCartData): Promise<void> {
    if (!this.initialized || this.isDormant || !this.conversionTracker) return;
    return this.conversionTracker.trackRemoveFromCart(
      data,
      this.cookieToken,
      this.locationId,
      this.locationName,
    );
  }

  async trackProductDetailsViewed(data: ProductViewData): Promise<void> {
    if (!this.initialized || this.isDormant || !this.conversionTracker) return;
    return this.conversionTracker.trackProductDetailsViewed(
      data,
      this.cookieToken,
      this.locationId,
      this.locationName,
    );
  }

  async trackThankYouPageViewed(data: ThankYouViewData): Promise<void> {
    if (!this.initialized || this.isDormant || !this.conversionTracker) return;
    return this.conversionTracker.trackThankYouPageViewed(
      data,
      this.cookieToken,
      this.locationId,
      this.locationName,
    );
  }

  async identify(user: Customer): Promise<void> {
    if (!this.initialized || this.isDormant || !this.http) return;

    try {
      if (
        !user.email ||
        typeof user.email !== "string" ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)
      ) {
        Logger.error("identify: invalid email address");
        return;
      }

      const sessionId = SessionManager.getOrCreate();

      const identifyPayload: Record<string, any> = {
        ...user,
        session_id: sessionId,
        cookie_token: this.cookieToken || "",
        email: user.email,
        name: user.name || "",
        user_id: user.userId || "",
        timestamp: new Date().toISOString(),
      };

      const identifyResponse = await this.http.post<IdentifyResponse>(
        API_PATH_IDENTIFY,
        identifyPayload,
      );

      this.isAttributed = identifyResponse.has_affiliate;

      if (identifyResponse.cookie_token) {
        this.cookieToken = identifyResponse.cookie_token;
        this.setTokenCookie(identifyResponse.cookie_token, this.cookieDuration);
      }

      if (identifyResponse.affiliate_code) {
        this.affiliateCode = identifyResponse.affiliate_code;
      }

      if (identifyResponse.discount_code) {
        this.discountCode = identifyResponse.discount_code;
        this.discountCache = null;
      }

      Logger.log("Customer identified", {
        email: user.email,
        isAttributed: this.isAttributed,
      });
    } catch (err) {
      Logger.warn("Identify call failed", err);

      // Fallback for identify() method too
      if (!this.cookieToken) {
        const fallbackToken = SessionManager.getOrCreate();
        this.cookieToken = fallbackToken;
        this.setTokenCookie(fallbackToken, this.cookieDuration);
        this.isAttributed = true;
        Logger.log("Identify failed - using session_id as cookie_token fallback");
      }
    }
  }

  async validateDiscount(code: string, total: number): Promise<DiscountValidationResult> {
    const invalid: DiscountValidationResult = {
      isValid: false,
      discountAmount: 0,
    };

    if (!this.initialized || this.isDormant || !this.http) return invalid;

    try {
      if (!code || typeof code !== "string") {
        Logger.error("validateDiscount: missing or invalid discount code");
        return invalid;
      }
      if (typeof total !== "number" || total <= 0) {
        Logger.error("validateDiscount: total must be a positive number");
        return invalid;
      }

      const response = await this.http.post<DiscountValidationAPIResponse>(
        API_PATH_DISCOUNT_VALIDATE,
        {
          code,
          order_total: total,
        },
      );

      Logger.log("Discount validated", response);

      return {
        isValid: response.is_valid,
        discountAmount: response.discount_amount,
      };
    } catch (err) {
      Logger.warn("Discount validation failed", err);
      return invalid;
    }
  }

  async destroy(): Promise<void> {
    if (!this.initialized) return;

    Logger.log("Destroying SDK...");

    try {
      AutoTracker.stop();

      await EventQueue.destroy();
      EventQueue.reset();

      SessionManager.destroy();
      CookieManager.delete(COOKIE_REF);
      CookieManager.delete(COOKIE_DISCOUNT);
      CookieManager.delete(COOKIE_TOKEN);
      try {
        localStorage.removeItem(COOKIE_TOKEN);
      } catch {
        // localStorage unavailable
      }
    } catch (err) {
      Logger.error("Error during SDK cleanup", err);
    } finally {
      this.initialized = false;
      this.isDormant = false;
      this.affiliateCode = null;
      this.cookieToken = null;
      this.discountCode = null;
      this.isAttributed = false;
      this.http = null;
      this.cookieDuration = DEFAULT_COOKIE_DURATION_DAYS;
      this.discountCache = null;
      this.conversionTracker = null;
      this.locationId = null;
      this.locationName = null;

      Logger.log("SDK destroyed — ready for re-initialization");
    }
  }
}
